import React, { useState, useEffect, useCallback } from 'react';
import { Layers, ChevronRight } from 'lucide-react';

// Elements to skip (not useful to show)
const SKIP_TAGS = new Set(['script', 'style', 'meta', 'link', 'head', '#comment', 'noscript']);

// Parse HTML string into a tree structure
function parseHtmlTree(html) {
  if (!html) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    return buildTree(body, '');
  } catch {
    return [];
  }
}

function buildTree(el, pathPrefix) {
  const nodes = [];
  if (!el || !el.children) return nodes;

  const tagCounters = {};
  Array.from(el.children).forEach((child, _) => {
    const tag = child.tagName?.toLowerCase();
    if (!tag || SKIP_TAGS.has(tag)) return;

    tagCounters[tag] = (tagCounters[tag] || 0) + 1;
  });

  const tagSeen = {};
  Array.from(el.children).forEach((child) => {
    const tag = child.tagName?.toLowerCase();
    if (!tag || SKIP_TAGS.has(tag)) return;

    tagSeen[tag] = (tagSeen[tag] || 0) + 1;
    const nthSuffix = tagCounters[tag] > 1 ? `:nth-of-type(${tagSeen[tag]})` : '';
    const id = child.id ? `#${child.id}` : '';
    const cls = typeof child.className === 'string'
      ? child.className.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(c => `.${c}`).join('')
      : '';

    const cssPath = pathPrefix
      ? `${pathPrefix} > ${tag}${id || nthSuffix}`
      : `${tag}${id || nthSuffix}`;

    const label = id ? `${tag}${id}` : cls ? `${tag}${cls}` : tag;
    const text = child.childElementCount === 0
      ? (child.textContent || '').trim().substring(0, 40)
      : '';

    nodes.push({
      tag,
      label,
      text,
      cssPath,
      children: buildTree(child, cssPath),
    });
  });

  return nodes;
}

function TreeNode({ node, depth, onHover, onClick, selectedCssPath }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedCssPath === node.cssPath;
  const indent = depth * 12;

  // Color coding by tag category
  const tagColor = () => {
    if (['section', 'main', 'article', 'aside', 'header', 'footer', 'nav'].includes(node.tag)) return 'text-purple-400';
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.tag)) return 'text-blue-400';
    if (['p', 'span', 'a', 'em', 'strong', 'b', 'i'].includes(node.tag)) return 'text-green-400';
    if (['img', 'video', 'picture', 'figure', 'svg'].includes(node.tag)) return 'text-yellow-400';
    if (['button', 'input', 'select', 'textarea', 'form', 'label'].includes(node.tag)) return 'text-orange-400';
    return 'text-[var(--orbis-t3)]';
  };

  return (
    <div>
      <div
        style={{ paddingLeft: 8 + indent + 'px' }}
        className={`flex items-center gap-1 py-0.5 pr-2 rounded cursor-pointer group transition-colors ${
          isSelected
            ? 'bg-[var(--orbis-amber)]/15 text-[var(--orbis-amber)]'
            : 'hover:bg-[var(--orbis-s2)]'
        }`}
        onMouseEnter={() => onHover(node.cssPath)}
        onMouseLeave={() => onHover(null)}
        onClick={() => {
          onClick(node.cssPath);
          if (hasChildren) setOpen(o => !o);
        }}
      >
        <span className="w-3 h-3 shrink-0 flex items-center justify-center">
          {hasChildren ? (
            <ChevronRight className={`w-2.5 h-2.5 text-[var(--orbis-t4)] transition-transform ${open ? 'rotate-90' : ''}`} />
          ) : (
            <span className="w-1 h-1 rounded-full bg-[var(--orbis-s3)] block" />
          )}
        </span>
        <span className={`text-[11px] font-mono shrink-0 ${tagColor()}`}>{node.tag}</span>
        {node.label !== node.tag && (
          <span className="text-[10px] text-[var(--orbis-t4)] truncate shrink min-w-0">
            {node.label.replace(node.tag, '')}
          </span>
        )}
        {node.text && (
          <span className="text-[10px] text-[var(--orbis-t4)]/60 truncate min-w-0 italic ml-1">
            "{node.text}"
          </span>
        )}
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((child, i) => (
            <TreeNode
              key={child.cssPath + i}
              node={child}
              depth={depth + 1}
              onHover={onHover}
              onClick={onClick}
              selectedCssPath={selectedCssPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function LayersPanel({ html, iframeRef, selectedCssPath, onSelectElement }) {
  const [tree, setTree] = useState([]);
  const [hoveredPath, setHoveredPath] = useState(null);

  useEffect(() => {
    if (html) {
      setTree(parseHtmlTree(html));
    }
  }, [html]);

  const handleHover = useCallback((cssPath) => {
    setHoveredPath(cssPath);
    if (iframeRef?.current?.contentWindow) {
      if (cssPath) {
        iframeRef.current.contentWindow.postMessage({ type: 'wf_highlight_element', cssPath }, '*');
      }
    }
  }, [iframeRef]);

  const handleClick = useCallback((cssPath) => {
    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'wf_select_element', cssPath }, '*');
    }
    if (onSelectElement) onSelectElement(cssPath);
  }, [iframeRef, onSelectElement]);

  if (!html) return null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[rgba(255,255,255,0.07)]">
        <Layers className="w-3.5 h-3.5 text-[var(--orbis-t3)]" />
        <span className="text-xs text-[var(--orbis-t2)] font-medium">Layers</span>
        <span className="ml-auto text-[10px] text-[var(--orbis-t4)]">Click to select</span>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {tree.length === 0 ? (
          <p className="text-[11px] text-[var(--orbis-t4)] text-center py-4">No elements found</p>
        ) : (
          tree.map((node, i) => (
            <TreeNode
              key={node.cssPath + i}
              node={node}
              depth={0}
              onHover={handleHover}
              onClick={handleClick}
              selectedCssPath={selectedCssPath}
            />
          ))
        )}
      </div>
    </div>
  );
}
